// Benches, hedges, flowers, palms, ducks and the fountain.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, add, canvasTex } from '../lib/factory.js';
import { instanced } from '../lib/instancing.js';
import { noiseFill } from '../textures/cloth.js';
import { waterMat } from '../materials/surfaces.js';
import { benches, ducks, flowers, hedges, jets, pads, palmSpots, tufts } from './eixample.js';
import { V } from './sagrada.js';
import { treeTime } from './trees.js';
import { mergeAny, tint } from './facade-details.js';

export let BENCH, benchGeo, makePalm, animatePark;

export function buildPark() {
  makePalm = function makePalm(sp) {
    const H = rr(4.6, 6),
      lean = rr(-0.35, 0.35),
      g = new THREE.Group();
    g.position.set(sp.x, 0.36, sp.z);
    g.rotation.y = rr(0, PI * 2);
    scene.add(g);
    const prof = [];
    for (let i = 0; i <= 16; i++) {
      const t = i / 16;
      prof.push(
        new THREE.Vector2(0.21 + 0.12 * Math.exp(-t * 10) + 0.02 * Math.sin(t * 14), t * H),
      );
    }
    const trunk = new THREE.LatheGeometry(prof, 18),
      tp = trunk.attributes.position;
    for (let i = 0; i < tp.count; i++) {
      const y = tp.getY(i) / H;
      tp.setX(i, tp.getX(i) + lean * y * y * H * 0.3);
    }
    trunk.computeVertexNormals();
    add(trunk, palmTrunkMat, 0, 0, 0, g);
    const top = V(lean * H * 0.3, H, 0);
    add(
      new THREE.SphereGeometry(0.34, 18, 14).scale(1, 1.15, 1),
      palmTrunkMat,
      top.x,
      top.y + 0.05,
      top.z,
      g,
    );
    const o = new THREE.Object3D(),
      fronds = [];
    for (let k = 0; k < 30; k++) {
      const ring = k < 9 ? 0 : k < 20 ? 1 : 2,
        pitch = [-0.95, -0.25, 0.3][ring] + rr(-0.15, 0.15);
      o.position.copy(top).add(V(0, 0.25 - ring * 0.12, 0));
      o.rotation.set(pitch, k * 2.399 + rr(-0.1, 0.1), rr(-0.2, 0.2), 'YXZ');
      o.scale.setScalar(rr(0.85, 1.1) * (ring === 0 ? 0.8 : 1));
      o.updateMatrix();
      fronds.push(frondGreen.clone().applyMatrix4(o.matrix));
    }
    for (let k = 0; k < 7; k++) {
      o.position.copy(top).add(V(0, -0.2, 0));
      o.rotation.set(1.25 + rr(-0.1, 0.2), k * 0.9 + rr(-0.2, 0.2), 0, 'YXZ');
      o.scale.setScalar(rr(0.6, 0.8));
      o.updateMatrix();
      fronds.push(frondDead.clone().applyMatrix4(o.matrix));
    }
    add(mergeGeometries(fronds), palmFrondMat, 0, 0, 0, g);
    for (let c = 0; c < 3; c++) {
      const a = c * 2.1 + rr(0, 0.5);
      for (let k = 0; k < 12; k++)
        add(
          gcacheDate(),
          dateMat,
          top.x + Math.cos(a) * 0.38 + rr(-0.08, 0.08),
          top.y - 0.25 - rr(0, 0.3),
          top.z + Math.sin(a) * 0.38 + rr(-0.08, 0.08),
          g,
        ).castShadow = false;
    }
  };
  animatePark = function animatePark(time) {
    waterMat.normalMap.offset.set(time * 0.02, time * 0.013);
    for (const d of ducks) {
      d.a += d.v * 0.016;
      const x = d.cx + Math.cos(d.a) * d.r * 1.5,
        z = d.cz + Math.sin(d.a) * d.r;
      d.g.position.set(x, Math.sin(time * 3 + d.r) * 0.015, z);
      d.g.rotation.y = Math.atan2(-Math.sin(d.a) * 1.5, Math.cos(d.a));
    } // heading = direction of travel (the head is at +z)
    for (const { j, pos, seeds, geo } of jetPts) {
      seeds.forEach(([a, ph, sp], i) => {
        const t = (time * 0.7 * sp + ph) % 1,
          r = 0.1 + t * 1.7;
        pos.set([j.cx + Math.cos(a) * r, 3.1 + t - t * t * 3.3, j.cz + Math.sin(a) * r], i * 3);
      });
      geo.attributes.position.needsUpdate = true;
    }
  };

  // park and promenade benches at human scale: a person is ~0.92 units (1.75 m), so the seat sits ~0.25 units (45 cm)
  // above the ground. BENCH scales the original design, which was almost twice that.
  // sit spots: y is the seat top less the depth of the hip bone
  BENCH = 0.62;
  benchGeo = () =>
    mergeAny([
      tint(new THREE.BoxGeometry(1.4, 0.08, 0.4).translate(0, 0.45, 0), 0x8a5a3a),
      tint(new THREE.BoxGeometry(1.4, 0.35, 0.06).translate(0, 0.7, -0.2), 0x8a5a3a),
      tint(new THREE.BoxGeometry(0.06, 0.45, 0.4).translate(-0.6, 0.22, 0), 0x2c3a33),
      tint(new THREE.BoxGeometry(0.06, 0.45, 0.4).translate(0.6, 0.22, 0), 0x2c3a33),
    ]).scale(BENCH, BENCH, BENCH);
  instanced(benchGeo(), M(0xffffff, { vertexColors: true }), benches, (o, b) => {
    o.position.set(b.x, 0.35, b.z);
    o.rotation.y = b.ry;
  });

  // park details: hedges, flower beds, grass tufts, lily pads, palms, ducks, fountain jets
  function swayMat(mat, amp, base) {
    mat.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = treeTime;
      sh.vertexShader =
        'uniform float uTime;\n' +
        sh.vertexShader.replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
      float ph = instanceMatrix[3].x * 1.37 + instanceMatrix[3].z * 1.23;
      transformed.x += sin(uTime * 2.2 + ph) * ${amp.toFixed(3)} * max(0., transformed.y - ${base.toFixed(3)});
      transformed.z += cos(uTime * 1.7 + ph) * ${(amp * 0.8).toFixed(3)} * max(0., transformed.y - ${base.toFixed(3)});`,
        );
    };
    return mat;
  }
  // hedges: a row of lumpy leafy masses (jittered spheres) with a painted leaf texture, instead of smooth boxes
  const leafTex = canvasTex(128, 128, (c) => {
    c.fillStyle = '#4c7a37';
    c.fillRect(0, 0, 128, 128);
    for (let k = 0; k < 420; k++) {
      const x = rnd() * 128,
        y = rnd() * 128,
        a = rr(0, PI);
      c.fillStyle = pick(['#3f6a2e', '#5b8d40', '#6ea04a', '#86b85a', '#355e28']);
      c.beginPath();
      c.ellipse(x, y, rr(2.5, 4.5), rr(1.2, 2), a, 0, PI * 2);
      c.fill();
    }
  });
  const hedgeG = mergeGeometries(
    [
      [-0.32, 0.02, 0, 0.3],
      [0, 0.06, 0.03, 0.33],
      [0.32, 0.0, -0.02, 0.3],
      [-0.15, 0.2, 0.05, 0.22],
      [0.18, 0.22, -0.04, 0.22],
    ].map(([x, y, z, r]) => {
      const g = new THREE.IcosahedronGeometry(r, 2),
        p = g.attributes.position;
      for (let i = 0; i < p.count; i++) {
        const k = 1 + (Math.sin(p.getX(i) * 31 + p.getY(i) * 17) * 0.5 + 0.5) * 0.14;
        p.setXYZ(i, p.getX(i) * k * 1.15, p.getY(i) * k, p.getZ(i) * k * 0.95);
      }
      g.computeVertexNormals();
      return g.translate(x, y, z);
    }),
  );
  for (const h of hedges) h.sy = rr(0.85, 1.15);
  const placeH = (o, h) => {
    o.position.set(h.x, 0.62, h.z);
    o.rotation.y = h.ry;
    o.scale.set(1, h.sy, 1);
  };
  instanced(
    hedgeG,
    M(0xffffff, { map: leafTex, flatShading: false, roughness: 0.85 }),
    hedges,
    placeH,
    [0xffffff, 0xe8f2dc, 0xdfeacf],
  );
  // a fringe of small leaf sprigs standing out of the surface breaks the smooth outline
  const sprigTex = canvasTex(64, 64, (c) => {
    for (let k = 0; k < 9; k++) {
      const a = -PI / 2 + rr(-1.1, 1.1),
        r = rr(10, 24),
        x = 32 + Math.cos(a) * r,
        y = 52 + Math.sin(a) * r;
      c.fillStyle = pick(['#4f8236', '#5f9440', '#6fa64a', '#3f6e2c', '#7db456']);
      c.save();
      c.translate(x, y);
      c.rotate(a + PI / 2);
      c.beginPath();
      c.ellipse(0, 0, 4, 9, 0, 0, PI * 2);
      c.fill();
      c.restore();
    }
  });
  const sprigs = [];
  for (let k = 0; k < 42; k++) {
    const t = rr(-1, 1),
      side = rnd() < 0.75 ? (rnd() < 0.5 ? -1 : 1) : 0,
      top = !side;
    const p = top
        ? new THREE.Vector3(t * 0.5, 0.33, rr(-0.2, 0.2))
        : new THREE.Vector3(t * 0.5, rr(-0.15, 0.25), side * 0.28),
      n = top
        ? new THREE.Vector3(rr(-0.3, 0.3), 1, rr(-0.3, 0.3))
        : new THREE.Vector3(rr(-0.3, 0.3), rr(0.2, 0.7), side);
    const g = new THREE.PlaneGeometry(0.2, 0.2);
    g.lookAt(n.normalize());
    g.translate(p.x, p.y, p.z);
    sprigs.push(g);
  }
  const sprigMesh = instanced(
    mergeGeometries(sprigs),
    M(0xffffff, {
      map: sprigTex,
      alphaTest: 0.5,
      side: THREE.DoubleSide,
      flatShading: false,
      roughness: 0.9,
    }),
    hedges,
    placeH,
  );
  sprigMesh.castShadow = false;
  {
    // flowers: a curved stem with two leaves and a yellow heart (vertex-coloured, one mesh), and a head of five
    // petals opening upward (a second mesh tinted per flower), each flower turned and tilted a little differently
    const green = (g, col = 0x4f8a35) => tint(g.index ? g.toNonIndexed() : g, col);
    const stemG = mergeGeometries([
      green(new THREE.CylinderGeometry(0.008, 0.011, 0.26, 5).translate(0, 0.13, 0).rotateZ(0.06)),
      green(
        new THREE.SphereGeometry(0.035, 6, 4)
          .scale(1, 0.18, 0.45)
          .translate(0.03, 0.08, 0)
          .rotateZ(-0.5),
        0x5c9a3e,
      ),
      green(
        new THREE.SphereGeometry(0.03, 6, 4)
          .scale(1, 0.18, 0.45)
          .translate(-0.028, 0.13, 0)
          .rotateZ(0.55),
        0x5c9a3e,
      ),
      green(new THREE.SphereGeometry(0.018, 8, 6).translate(0.015, 0.275, 0), 0xf2c230),
    ]);
    const petalG = mergeGeometries(
      [...Array(5)].map((_, k) =>
        new THREE.SphereGeometry(0.035, 8, 6)
          .scale(1, 0.22, 0.55)
          .translate(0.042, 0, 0)
          .rotateZ(0.35)
          .rotateY((k * PI * 2) / 5)
          .translate(0.015, 0.27, 0),
      ),
    );
    for (const f of flowers) f.rot = [rr(-0.15, 0.15), rr(0, PI * 2), rr(-0.15, 0.15)]; // shared by stem and head
    const placeF = (o, f) => {
      o.position.set(f.x, 0.45, f.z);
      o.rotation.set(...f.rot);
      o.scale.setScalar(f.s);
    };
    const fs = instanced(
      stemG,
      swayMat(M(0xffffff, { vertexColors: true, flatShading: false, roughness: 0.7 }), 0.18, 0),
      flowers,
      placeF,
    );
    fs.castShadow = false;
    const fp = instanced(
      petalG,
      swayMat(M(0xffffff, { flatShading: false, roughness: 0.55 }), 0.18, 0),
      flowers,
      placeF,
      [0xe8445a, 0xf5c542, 0xf28c28, 0xffffff, 0xc04fd6, 0xff7aa8, 0xff9ec4],
    );
    fp.castShadow = false;
    const blade = (rx, rz) =>
      new THREE.ConeGeometry(0.035, 0.32, 3).translate(0, 0.16, 0).rotateX(rx).rotateZ(rz);
    const tf = instanced(
      mergeGeometries([
        blade(0, 0),
        blade(0.35, 0.2),
        blade(-0.3, -0.25),
        blade(0.1, 0.45),
        blade(-0.15, -0.5),
      ]),
      swayMat(M(0xffffff, { flatShading: false, roughness: 0.9 }), 0.25, 0),
      tufts,
      (o, t) => {
        o.position.set(t.x, 0.45, t.z);
        o.rotation.y = t.r;
        o.scale.setScalar(t.s);
      },
      [0x6f9f45, 0x7fb050, 0x5e8f3c, 0x8fbf5a],
    );
    tf.castShadow = false;
    instanced(
      new THREE.CylinderGeometry(0.32, 0.32, 0.02, 20, 1, false, 0.3, PI * 2 - 0.3),
      M(0x4f9a45, { flatShading: false }),
      pads,
      (o, p) => {
        o.position.set(p.x, 0.48, p.z);
        o.rotation.y = p.r;
        o.scale.setScalar(p.s);
      },
    ).castShadow = false;
  }
  // palm trees (date palms, as on Barcelona's promenades): diamond-scarred trunk, pinnate fronds, dead-frond skirt, dates
  const palmBark = canvasTex(128, 256, (c) => {
    c.fillStyle = '#8a6e4c';
    c.fillRect(0, 0, 128, 256);
    for (let row = 0; row < 16; row++)
      for (let col = 0; col < 8; col++) {
        const x = col * 16 + (row % 2) * 8,
          y = row * 16;
        c.fillStyle = pick(['#a3865f', '#957852', '#b09470']);
        c.beginPath();
        c.moveTo(x + 8, y);
        c.lineTo(x + 16, y + 8);
        c.lineTo(x + 8, y + 16);
        c.lineTo(x, y + 8);
        c.closePath();
        c.fill();
        c.strokeStyle = 'rgba(50,35,20,.6)';
        c.lineWidth = 1.5;
        c.stroke();
      }
    noiseFill(c, 128, 256, 1200, ['rgba(40,30,20,.25)', 'rgba(220,200,160,.2)'], 1);
  });
  palmBark.repeat.set(3, 4);
  function frondGeometry(tipCol, baseCol) {
    const pos = [],
      cols = [],
      L = 2.9,
      N = 22,
      cA = new THREE.Color(baseCol),
      cB = new THREE.Color(tipCol),
      cc = new THREE.Color();
    const P = (t) => new THREE.Vector3(0, 0.55 * t - 1.5 * t * t, L * t);
    for (let i = 2; i < N; i++) {
      const t = i / N,
        p = P(t),
        f = P(Math.min(1, t + 0.02))
          .sub(p)
          .normalize(),
        side = new THREE.Vector3(1, 0, 0);
      const ll = 0.62 * Math.sin(Math.min(1, t * 1.25) * PI * 0.9) + 0.06,
        w = 0.028;
      for (const sx of [-1, 1]) {
        const d = side
            .clone()
            .multiplyScalar(sx)
            .addScaledVector(f, 0.45)
            .add(V(0, -0.18, 0))
            .normalize(),
          tip = p.clone().addScaledVector(d, ll);
        const up = V(0, w, 0),
          q = [
            p.clone().add(up),
            p.clone().sub(up),
            tip.clone().addScaledVector(up, 0.2),
            tip.clone().addScaledVector(up, -0.2),
          ];
        for (const k of [0, 1, 2, 1, 3, 2]) {
          pos.push(q[k].x, q[k].y, q[k].z);
          cc.copy(cA).lerp(cB, (k >= 2 ? 0.6 : 0) + t * 0.4);
          cols.push(cc.r, cc.g, cc.b);
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
    g.setAttribute(
      'uv',
      new THREE.Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2),
    );
    g.computeVertexNormals();
    const rach = new THREE.TubeGeometry(
      new THREE.CatmullRomCurve3([0, 0.25, 0.5, 0.75, 1].map(P)),
      16,
      0.022,
      5,
    ).toNonIndexed();
    const rc = new Float32Array(rach.attributes.position.count * 3);
    for (let i = 0; i < rc.length; i += 3) rc.set([0.55, 0.5, 0.28], i);
    rach.setAttribute('color', new THREE.BufferAttribute(rc, 3));
    return mergeGeometries([g, rach]);
  }
  const frondGreen = frondGeometry(0x9cbf5a, 0x3e7a32),
    frondDead = frondGeometry(0xb49562, 0x7a5e3a);
  const palmFrondMat = M(0xffffff, {
    vertexColors: true,
    side: THREE.DoubleSide,
    flatShading: false,
    roughness: 0.7,
  });
  const palmTrunkMat = M(0xffffff, { map: palmBark, flatShading: false, roughness: 0.9 }),
    dateMat = M(0xd98a2a, { flatShading: false, roughness: 0.5 });
  const gcacheDate = (() => {
    let d;
    return () => (d ??= new THREE.SphereGeometry(0.045, 8, 6).scale(1, 1.3, 1));
  })();
  palmSpots.forEach(makePalm);
  // ducks paddling around the pond
  // Mallards: drakes with a glossy green head, white collar, chestnut breast, grey body and a blue wing bar; mottled
  // brown ducks. A teardrop body riding low in the water, upturned tail, flat orange-yellow bill, beady eyes.
  const fM = (c, o = {}) => M(c, Object.assign({ flatShading: false, roughness: 0.7 }, o));
  const mottle = canvasTex(64, 64, (c) => {
    c.fillStyle = '#9a7650';
    c.fillRect(0, 0, 64, 64);
    for (let k = 0; k < 160; k++) {
      c.fillStyle = pick(['#6e5236', '#b8956a', '#5a4128', '#c9a77a']);
      c.beginPath();
      c.ellipse(rnd() * 64, rnd() * 64, rr(1.5, 3), rr(0.8, 1.6), rr(0, PI), 0, PI * 2);
      c.fill();
    }
  });
  const duckMats = {
    grey: fM(0xb9b6ae),
    breast: fM(0x7a4a32),
    head: fM(0x1f6b45, { roughness: 0.35 }),
    collar: fM(0xf4f2ec),
    wing: fM(0x8c8478),
    bar: fM(0x2f4fa8, { roughness: 0.3 }),
    tail: fM(0x2a2a2c),
    bill: fM(0xe8b23a),
    billF: fM(0xd98a32),
    eye: fM(0x111111, { roughness: 0.2 }),
    hen: fM(0xffffff, { map: mottle }),
    henHead: fM(0x8a6a46),
  };
  ducks.forEach((d, i) => {
    const g = new THREE.Group();
    scene.add(g);
    d.g = g;
    const drake = i % 3 !== 2,
      D = duckMats,
      body = drake ? D.grey : D.hen;
    add(new THREE.SphereGeometry(0.2, 18, 12).scale(1, 0.62, 1.45), body, 0, 0.5, -0.02, g); // body
    add(
      new THREE.SphereGeometry(0.15, 16, 12).scale(1, 0.9, 1),
      drake ? D.breast : D.hen,
      0,
      0.53,
      0.17,
      g,
    ); // breast
    add(
      new THREE.SphereGeometry(0.07, 10, 8).scale(0.8, 0.45, 1.4).rotateX(-0.55),
      drake ? D.tail : D.hen,
      0,
      0.6,
      -0.3,
      g,
    ); // upturned tail
    if (drake)
      add(
        new THREE.TorusGeometry(0.025, 0.007, 5, 10, PI * 1.3).rotateY(PI / 2),
        D.tail,
        0,
        0.66,
        -0.33,
        g,
      ); // drake's curl
    for (const sx of [-1, 1]) {
      add(
        new THREE.SphereGeometry(0.13, 12, 8).scale(0.35, 0.5, 1.2),
        drake ? D.wing : D.hen,
        sx * 0.15,
        0.58,
        -0.06,
        g,
      ); // folded wing
      add(new THREE.BoxGeometry(0.012, 0.035, 0.1), D.bar, sx * 0.195, 0.6, -0.04, g); // speculum
    }
    add(
      new THREE.CylinderGeometry(0.045, 0.065, 0.1, 12).rotateX(0.3),
      drake ? D.head : D.henHead,
      0,
      0.64,
      0.22,
      g,
    ); // neck
    if (drake)
      add(
        new THREE.TorusGeometry(0.052, 0.012, 6, 16).rotateX(PI / 2 + 0.3),
        D.collar,
        0,
        0.6,
        0.21,
        g,
      ); // collar
    add(
      new THREE.SphereGeometry(0.075, 16, 12).scale(0.95, 1, 1.15),
      drake ? D.head : D.henHead,
      0,
      0.72,
      0.26,
      g,
    ); // head
    add(
      new THREE.SphereGeometry(0.045, 10, 6).scale(0.8, 0.3, 1.5).translate(0, 0, 0.05),
      drake ? D.bill : D.billF,
      0,
      0.7,
      0.31,
      g,
    ); // bill
    for (const sx of [-1, 1])
      add(new THREE.SphereGeometry(0.012, 8, 6), D.eye, sx * 0.056, 0.74, 0.3, g);
    g.scale.setScalar(0.85);
  });
  // fountain jets: particles on parabolic arcs
  const jetPts = [];
  for (const j of jets) {
    const N = 260,
      pos = new Float32Array(N * 3),
      seeds = Array.from({ length: N }, () => [rr(0, PI * 2), rr(0, 1), rr(0.8, 1.2)]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const pts = new THREE.Points(
      geo,
      new THREE.PointsMaterial({
        color: 0xd8f0ff,
        size: 0.09,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
      }),
    );
    scene.add(pts);
    jetPts.push({ j, pos, seeds, geo });
  }
}
